import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import styles from './Auth.module.scss'
import useSignUp from './hooks/useSignUp'
import useSignIn from './hooks/useSignIn'
import { ROUTES } from '../../../router/routes'
import { getAppSumoData } from '../../../utils/appsumo/AppSumoManager'
import { CreateWorkspace } from './CreateWorkspace/CreateWorkspace'
import { RecipeNavbar } from '../Recipe/Recipe'
import { useRecoverPassword } from './hooks/useRecoverPassword'
import { EVENTS, trackEvent } from '../../../events'
import { VerifyEmail } from './VerifyEmail/VerifyEmail'
import { RecoverPassword } from './RecoverPassword/RecoverPassword'
import { SignupForm } from './SignupForm/SignupForm'
import { LoginForm } from './LoginForm/LoginForm'
import { ValueSection } from './ValueSection/ValueSection'

export const Auth = ({ loginRoute }) => {
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)
    const navigate = useNavigate()
    const location = useLocation()
    const appSumoData = getAppSumoData(location)
    const [step, setStep] = useState(0)
    const [recoverPasswordStep, setRecoverPasswordStep] = useState(0)
    const [isLogin, setIsLogin] = useState(loginRoute)
    const [errors, setErrors] = useState({
        fullName: '',
        email: '',
        password: '',
    })

    // Get invitation parameters from URL or localStorage
    const searchParams = new URLSearchParams(location.search)
    const invitationToken = searchParams.get('token') || localStorage.getItem('pendingInvitation')
    const invitationEmail = searchParams.get('email')
    const hasInvitation = !!invitationToken

    // this is true if the product was entered with forceLogin=[email] in the URL
    const [forceUserEmail, setForceUserEmail] = useState(null)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [forceUserName, setFoceUserName] = useState(null)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [forcePassword, setForcePassword] = useState(null)

    const {
        signUpUser,
        handleSignUpInputChange,
        handleSignUp,
        isSignUpLoading,
        handleConfirmSignup,
        handleGoToCanvas,
        handleResendCode,
    } = useSignUp(step, setStep, setErrors, invitationToken)

    const { signInUser, handleSignInInputChange, handleSignIn, isSignInLoading } =
        useSignIn(setErrors)

    const {
        email,
        emailError,
        newPassword,
        handleEmailChange,
        handleRecoverPasswordChange,
        handleStartPWDReset,
        handleFinishPWDReset,
    } = useRecoverPassword(setRecoverPasswordStep, () => {
        setIsLogin(true)
        setStep(0)
    })

    // Pre-fill email from invitation
    useEffect(() => {
        if (invitationEmail && !isLogin) {
            handleSignUpInputChange('email', invitationEmail)
        }
    }, [invitationEmail, isLogin])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search)
        const recipeMapping = searchParams.get('recipeMapping')
        // forceLogin is a query param that forces the user to login as a certain user email; it may be in a query or might have been set in local storage
        const forceLogin = searchParams.get('forceLogin') || localStorage.getItem('forceLogin')

        if (forceLogin) {
            if (forceLogin.includes('|')) {
                setForceUserEmail(forceLogin.split('|')[0])
                setFoceUserName(forceLogin.split('|')[1])
                handleSignInInputChange('email', forceLogin.split('|')[0])
                handleSignUpInputChange('email', forceLogin.split('|')[0])
                handleSignUpInputChange('fullName', forceLogin.split('|')[1])
            } else {
                setForceUserEmail(forceLogin)
                handleSignInInputChange('email', forceLogin)
                handleSignUpInputChange('email', forceLogin.split('|')[0])
            }
            // monday account may be encoded as last param
            try {
                localStorage.setItem('mondayAccount', forceLogin.split('|')[2])
            } catch {}

            localStorage.removeItem('forceLogin')
        }

        // filledLogin is a query param that forces the user to login as a certain user email and password which we pass in (e.g. from our web form when we auto-create a user account); it may be in a query or might have been set in local storage
        let filledLogin = searchParams.get('filledLogin') || localStorage.getItem('filledLogin')

        // e.g. gabe@scoop.report|d****|ScoopWebsite|61cb586e-307a-4dd5-99be-044c8aba5ab3|W77|Gabe Test Samples|C200
        if (filledLogin) {
            filledLogin = decodeURIComponent(filledLogin)
            if (filledLogin.includes('|')) {
                setForceUserEmail(filledLogin.split('|')[0])
                setForcePassword(filledLogin.split('|')[1])
                handleSignInInputChange('email', filledLogin.split('|')[0])
                handleSignInInputChange('password', filledLogin.split('|')[1])
            } else {
                setForceUserEmail(filledLogin)
                handleSignInInputChange('email', filledLogin)
            }
            try {
                localStorage.setItem(
                    'lastUsedWorkspaces',
                    JSON.stringify({
                        dataType: 'Map',
                        value: [
                            [
                                filledLogin.split('|')[3],
                                [filledLogin.split('|')[4], filledLogin.split('|')[5]],
                            ],
                        ],
                    })
                )
            } catch {}
            try {
                localStorage.setItem('lastCanvas', filledLogin.split('|')[6])
            } catch {}
            try {
                localStorage.setItem('origin', filledLogin.split('|')[2])
            } catch {}

            localStorage.removeItem('filledLogin')
        }

        if (recipeMapping) {
            localStorage.setItem('recipeMapping', recipeMapping)
            trackEvent(EVENTS.CANVAS.RECIPE_PRODUCT_INVOCATION, {
                recipe: {
                    name: recipeMapping,
                },
            })
        }

        if (isAuthenticated && isLogin) navigate(ROUTES.INSIGHTS)
    }, [isAuthenticated, isLogin])

    // Force signup tab if coming from AppSumo
    useEffect(() => {
        if (location.state?.showSignup) {
            setIsLogin(false) // This shows the signup form
        }
    }, [location.state])

    // Update isLogin state when loginRoute prop changes (e.g., navigating between /login and /signup)
    useEffect(() => {
        setIsLogin(loginRoute)
    }, [loginRoute])

    const selectOption = (option) => {
        if (option === 0) {
            handleGoToCanvas()
        } else if (option === 1) {
            navigate(ROUTES.RECIPE)
        }
    }

    const toggleMode = () => {
        setIsLogin(!isLogin)
        setErrors({
            fullName: '',
            email: '',
            password: '',
        })
    }

    const renderSignUp = () => {
        if (isLogin) {
            return (
                <LoginForm
                    onToggleMode={toggleMode}
                    signInUser={signInUser}
                    handleSignInInputChange={handleSignInInputChange}
                    handleSignIn={handleSignIn}
                    isSignInLoading={isSignInLoading}
                    errors={errors}
                    setStep={setStep}
                    forceUserEmail={forceUserEmail}
                    hasInvitation={hasInvitation}
                />
            )
        } else {
            return (
                <SignupForm
                    onToggleMode={toggleMode}
                    appSumoData={appSumoData}
                    signUpUser={signUpUser}
                    handleSignUpInputChange={handleSignUpInputChange}
                    handleSignUp={handleSignUp}
                    isSignUpLoading={isSignUpLoading}
                    errors={errors}
                    forceUserEmail={forceUserEmail}
                    invitationEmail={invitationEmail}
                    hasInvitation={hasInvitation}
                />
            )
        }
    }

    // Step 1: Verify Email
    if (step === 1) {
        return (
            <div className={styles.pageWrapper}>
                <div className={styles.signupWrapper}>
                    <ValueSection />
                    <VerifyEmail
                        signUpUser={signUpUser}
                        isSignUpLoading={isSignUpLoading}
                        handleConfirmSignup={handleConfirmSignup}
                        handleResendCode={handleResendCode}
                    />
                </div>
            </div>
        )
    }

    // Step 2: Recover Password
    if (step === 2) {
        return (
            <div className={styles.pageWrapper}>
                <div className={styles.signupWrapper}>
                    <ValueSection />
                    <RecoverPassword
                        recoverPasswordStep={recoverPasswordStep}
                        email={email}
                        emailError={emailError}
                        newPassword={newPassword}
                        handleEmailChange={handleEmailChange}
                        handleRecoverPasswordChange={handleRecoverPasswordChange}
                        handleStartPWDReset={handleStartPWDReset}
                        handleFinishPWDReset={handleFinishPWDReset}
                        setStep={setStep}
                        setIsLogin={setIsLogin}
                        isSignUpLoading={isSignUpLoading}
                        isSignInLoading={isSignInLoading}
                    />
                </div>
            </div>
        )
    }

    // Step 3+: Create Workspace
    if (step >= 3) {
        return (
            <div>
                <RecipeNavbar goBack={() => setStep(step - 1)} showIcon={true} />
                <CreateWorkspace selectOption={selectOption} />
            </div>
        )
    }

    // Step 0: Login/Signup
    return (
        <div className={styles.pageWrapper}>
            <div className={styles.signupWrapper}>
                <ValueSection />
                {renderSignUp()}
            </div>
        </div>
    )
}
